import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  TextField,
  MenuItem,
  Stack,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
} from '@mui/material';

// Icônes Material-UI
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CancelIcon from '@mui/icons-material/Cancel';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FaceIcon from '@mui/icons-material/Face';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PrintIcon from '@mui/icons-material/Print';
import VideocamIcon from '@mui/icons-material/Videocam';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import StarIcon from '@mui/icons-material/Star';
import WorkIcon from '@mui/icons-material/Work';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

import { useAuth } from '../../contexts/AuthContext';

const HistoriqueReservations = () => {
  const { user } = useAuth();
  const userRole = Number(user?.role || user?.role_user);
  const userId = user?.id_user || user?.id;

  // Définition dynamique du chemin de rôle pour l'API
  const rolePath = [1, 2].includes(userRole)
    ? 'admin'
    : userRole === 4
      ? 'praticien'
      : 'intervenant';

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedIntervenant, setSelectedIntervenant] = useState('all');

  // Modale Détails Réservation
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  // Modale Compte Rendu
  const [openCRModal, setOpenCRModal] = useState(false);
  const [selectedCR, setSelectedCR] = useState(null);
  const [crReservationParent, setCrReservationParent] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'YOUR_API_URL';
  const token = localStorage.getItem('token');

  // 1. Chargement des réservations
  useEffect(() => {
    const fetchReservationsHistory = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${apiUrl}/${rolePath}/offer/resevration/l/all`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        const data = await response.json();

        if (data.status === 'success' || Array.isArray(data.result)) {
          setReservations(data.result || []);
        } else if (Array.isArray(data)) {
          setReservations(data);
        }
      } catch (error) {
        console.error(
          "Erreur lors du chargement de l'historique des réservations:",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    fetchReservationsHistory();
  }, [apiUrl, token]);

  const getRoleLabel = (userObj) => {
  const r = Number(userObj?.role_user || userObj?.role);
  if (r === 4) return 'Praticien';
  if (r === 3) return 'Intervenant';
  if ([1, 2].includes(r)) return 'Administrateur';
  return 'Intervenant / Praticien';
};

  // 2. Filtrage selon les rôles
  const roleFilteredReservations = useMemo(() => {
    if ([1, 2].includes(userRole)) {
      return reservations;
    }
    return reservations.filter(
      (res) =>
        String(res.user_id) === String(userId) ||
        String(res.offre?.id_intervenant) === String(userId) ||
        String(res.user?.id_user) === String(userId)
    );
  }, [reservations, userRole, userId]);

  // 3. Liste des intervenants / praticiens pour le filtre
  const intervenantsList = useMemo(() => {
    const map = new Map();
    roleFilteredReservations.forEach((res) => {
      const u = res.user;
      if (u && u.id_user) {
        const roleLabel = getRoleLabel(u);
        const fullName =
          `${u.prenom_user || ''} ${u.nom_user || ''}`.trim() || u.email_user;
        map.set(u.id_user, `${fullName} (${roleLabel})`);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [roleFilteredReservations]);

  // 4. Filtrage par Recherche / Statut / Intervenant
  const filteredReservations = useMemo(() => {
    return roleFilteredReservations.filter((res) => {
      if (
        selectedStatus !== 'all' &&
        res.status_reservation !== selectedStatus
      ) {
        return false;
      }

      if (
        selectedIntervenant !== 'all' &&
        String(res.user_id) !== String(selectedIntervenant) &&
        String(res.user?.id_user) !== String(selectedIntervenant)
      ) {
        return false;
      }

      if (searchTerm.trim() !== '') {
        const query = searchTerm.toLowerCase();
        const clientName =
          `${res.client?.prenom_client || ''} ${res.client?.nom_client || ''}`.toLowerCase();
        const clientEmail = (res.client?.email_client || '').toLowerCase();
        const offreTitle = (
          res.offre?.info_offre?.titre_offre || ''
        ).toLowerCase();
        const userIntervenant =
          `${res.user?.prenom_user || ''} ${res.user?.nom_user || ''}`.toLowerCase();

        return (
          clientName.includes(query) ||
          clientEmail.includes(query) ||
          offreTitle.includes(query) ||
          userIntervenant.includes(query)
        );
      }

      return true;
    });
  }, [
    roleFilteredReservations,
    selectedStatus,
    selectedIntervenant,
    searchTerm,
  ]);

  // 5. Calcul des compteurs
  const counts = useMemo(() => {
    const total = filteredReservations.length;
    const pending = filteredReservations.filter(
      (r) => r.status_reservation === 'pending'
    ).length;
    const cancelled = filteredReservations.filter(
      (r) => r.status_reservation === 'cancelled'
    ).length;
    const confirmed = filteredReservations.filter(
      (r) => r.status_reservation === 'confirmed'
    ).length;
    const completed = filteredReservations.filter(
      (r) => r.status_reservation === 'completed'
    ).length;

    return { total, pending, cancelled, confirmed, completed };
  }, [filteredReservations]);

  // 6. Regroupement par Offre
  const groupedByOffre = useMemo(() => {
    const groups = {};

    filteredReservations.forEach((res) => {
      const offreId = res.id_offre || res.offre?.id_offre || 'sans_offre';
      const titreOffre =
        res.offre?.info_offre?.titre_offre || 'Offre non spécifiée';

      if (!groups[offreId]) {
        const u = res.user;
        const roleLabel = getRoleLabel(u);
        const intervenantName = u
          ? `${u.prenom_user || ''} ${u.nom_user || ''}`.trim()
          : 'Non assigné';

        groups[offreId] = {
          offreId,
          titreOffre,
          offreDetails: res.offre,
          intervenantDisplay: `${roleLabel}: ${intervenantName}`,
          reservations: [],
        };
      }
      groups[offreId].reservations.push(res);
    });

    return Object.values(groups);
  }, [filteredReservations]);

  // Modale détails réservation
  const handleOpenDetails = (res) => {
    setSelectedReservation(res);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedReservation(null);
  };

  // Modale Compte Rendu
  const handleOpenCRModal = (compteRendu, resParent) => {
    setSelectedCR(compteRendu);
    setCrReservationParent(resParent);
    setOpenCRModal(true);
  };

  const handleCloseCRModal = () => {
    setOpenCRModal(false);
    setSelectedCR(null);
    setCrReservationParent(null);
  };
const handlePrintPDF = () => {
    const printElement = document.getElementById('printable-compte-rendu');
    if (!printElement) return;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <html>
        <head>
          <title>Compte Rendu - Séance n°${selectedCR?.numero_seance || 1}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #2B3674; margin: 0; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #4318FF; padding-bottom: 10px; margin-bottom: 20px; }
            .title { font-size: 18px; font-weight: bold; color: #4318FF; }
            .subtitle { font-size: 14px; font-weight: bold; color: #2B3674; margin-top: 4px; }
            .badge { background: #E6F9F5; color: #05CD99; padding: 6px 12px; border-radius: 12px; font-size: 12px; font-weight: bold; border: 1px solid #B2F1E4; }
            .card { background: #F4F7FE; padding: 15px; border-radius: 12px; margin-bottom: 20px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 13px; }
            .section { padding: 12px 15px; border-radius: 8px; margin-bottom: 15px; }
            .sec-1 { background: #F8FAFC; border-left: 4px solid #0284C7; }
            .sec-2 { background: #F0F9FF; border-left: 4px solid #0EA5E9; }
            .sec-3 { background: #F0FDF4; border-left: 4px solid #10B981; }
            .sec-4 { background: #EEF2FF; border-left: 4px solid #6366F1; }
            .sec-title { font-weight: bold; margin-bottom: 6px; font-size: 13px; }
            
            /* CORRECTION : Redimensionnement ou masquage des icônes SVG MUI */
            svg {
              width: 16px !important;
              height: 16px !important;
              max-width: 16px !important;
              max-height: 16px !important;
              vertical-align: middle;
              margin-right: 6px;
            }

            .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #A3AED0; border-top: 1px solid #E2E8F0; padding-top: 10px; }
          </style>
        </head>
        <body>
          ${printElement.innerHTML}
          <div class="footer">Plateforme S.O.A (Soins d'Onco-Accompagnement) • Document officiel généré le ${new Date().toLocaleDateString('fr-FR')}</div>
        </body>
      </html>
    `);
    doc.close();

    iframe.contentWindow.focus();
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 200);
  };

  // Rendu des badges de statuts
  const renderStatusChip = (status) => {
    switch (status) {
      case 'pending':
        return (
          <Chip
            icon={<AccessTimeIcon sx={{ color: '#FFB547 !important' }} />}
            label="En attente"
            size="small"
            sx={{
              bgcolor: '#FFF9EC',
              color: '#FFB547',
              fontWeight: 700,
              border: '1px solid #FFE8B8',
            }}
          />
        );
      case 'cancelled':
        return (
          <Chip
            icon={<CancelIcon sx={{ color: '#EE5D50 !important' }} />}
            label="Annulé"
            size="small"
            sx={{
              bgcolor: '#FEEFEE',
              color: '#EE5D50',
              fontWeight: 700,
              border: '1px solid #FDCFCB',
            }}
          />
        );
      case 'confirmed':
        return (
          <Chip
            icon={<CheckCircleIcon sx={{ color: '#05CD99 !important' }} />}
            label="Confirmé"
            size="small"
            sx={{
              bgcolor: '#E6F9F5',
              color: '#05CD99',
              fontWeight: 700,
              border: '1px solid #B2F1E4',
            }}
          />
        );
      case 'completed':
        return (
          <Chip
            icon={<TaskAltIcon sx={{ color: '#7052FF !important' }} />}
            label="Terminé"
            size="small"
            sx={{
              bgcolor: '#F0ECFF',
              color: '#7052FF',
              fontWeight: 700,
              border: '1px solid #D9CEFF',
            }}
          />
        );
      default:
        return <Chip label={status || 'Inconnu'} size="small" />;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '80vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#4318FF' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, bgcolor: '#F4F7FE', minHeight: '100vh' }}>
      {/* En-tête */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 800, color: '#2B3674', mb: 1 }}
        >
          Historique des Réservations
        </Typography>
        <Typography variant="body2" sx={{ color: '#A3AED0' }}>
          {[1, 2].includes(userRole)
            ? "Consultez et filtrez l'ensemble des réservations par soin individuel et par praticien/intervenant."
            : "Consultez l'historique de toutes vos réservations classées par soin individuel."}
        </Typography>
      </Box>

      {/* Cartes de statistiques */}
      <Stack
        direction={{ xs: 'column', xl: 'row' }}
        spacing={2}
        sx={{ mb: 4, width: '100%' }}
      >
        <Card
          sx={{
            flex: 1,
            borderRadius: '20px',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.03)',
            border: '1px solid #ECEEF0',
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#A3AED0',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                  }}
                >
                  Total Réservations
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, color: '#2B3674', mt: 0.5 }}
                >
                  {counts.total}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: 'rgba(67, 24, 255, 0.1)',
                  color: '#4318FF',
                  width: 42,
                  height: 42,
                }}
              >
                <EqualizerIcon />
              </Avatar>
            </Stack>
          </CardContent>
        </Card>

        <Card
          sx={{
            flex: 1,
            borderRadius: '20px',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.03)',
            border: '1px solid #FFE8B8',
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#FFB547',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                  }}
                >
                  Réservations En Attente
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, color: '#FFB547', mt: 0.5 }}
                >
                  {counts.pending}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: '#FFF9EC',
                  color: '#FFB547',
                  width: 42,
                  height: 42,
                }}
              >
                <AccessTimeIcon />
              </Avatar>
            </Stack>
          </CardContent>
        </Card>

        <Card
          sx={{
            flex: 1,
            borderRadius: '20px',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.03)',
            border: '1px solid #FDCFCB',
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#EE5D50',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                  }}
                >
                  Réservations Annulées
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, color: '#EE5D50', mt: 0.5 }}
                >
                  {counts.cancelled}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: '#FEEFEE',
                  color: '#EE5D50',
                  width: 42,
                  height: 42,
                }}
              >
                <CancelIcon />
              </Avatar>
            </Stack>
          </CardContent>
        </Card>

        <Card
          sx={{
            flex: 1,
            borderRadius: '20px',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.03)',
            border: '1px solid #B2F1E4',
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#05CD99',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                  }}
                >
                  Réservations Confirmées
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, color: '#05CD99', mt: 0.5 }}
                >
                  {counts.confirmed}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: '#E6F9F5',
                  color: '#05CD99',
                  width: 42,
                  height: 42,
                }}
              >
                <CheckCircleIcon />
              </Avatar>
            </Stack>
          </CardContent>
        </Card>

        <Card
          sx={{
            flex: 1,
            borderRadius: '20px',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.03)',
            border: '1px solid #D9CEFF',
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#7052FF',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                  }}
                >
                  Réservations Terminées
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, color: '#7052FF', mt: 0.5 }}
                >
                  {counts.completed}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: '#F0ECFF',
                  color: '#7052FF',
                  width: 42,
                  height: 42,
                }}
              >
                <TaskAltIcon />
              </Avatar>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Barre de Recherche et Filtres */}
      <Paper
        sx={{
          p: 2.5,
          mb: 4,
          borderRadius: '16px',
          boxShadow: '0px 4px 12px rgba(0,0,0,0.03)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems="center"
        >
          <TextField
            fullWidth
            placeholder="Rechercher par client, email, offre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#A3AED0' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: '#F4F7FE',
              borderRadius: '12px',
              '& fieldset': { border: 'none' },
            }}
          />

          <TextField
            select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            sx={{
              minWidth: 200,
              bgcolor: '#F4F7FE',
              borderRadius: '12px',
              '& fieldset': { border: 'none' },
            }}
          >
            <MenuItem value="all">Tous les statuts</MenuItem>
            <MenuItem value="pending">En attente</MenuItem>
            <MenuItem value="cancelled">Annulé</MenuItem>
            <MenuItem value="confirmed">Confirmé</MenuItem>
            <MenuItem value="completed">Terminé</MenuItem>
          </TextField>

          {[1, 2].includes(userRole) && (
            <TextField
              select
              value={selectedIntervenant}
              onChange={(e) => setSelectedIntervenant(e.target.value)}
              sx={{
                minWidth: 260,
                bgcolor: '#F4F7FE',
                borderRadius: '12px',
                '& fieldset': { border: 'none' },
              }}
            >
              <MenuItem value="all">
                Tous les praticiens / intervenants
              </MenuItem>
              {intervenantsList.map((inter) => (
                <MenuItem key={inter.id} value={inter.id}>
                  {inter.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      </Paper>

      {/* Accordéons */}
      {groupedByOffre.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: '16px' }}>
          <Typography color="textSecondary">
            Aucune réservation trouvée pour ces critères.
          </Typography>
        </Paper>
      ) : (
        groupedByOffre.map((group) => {
          const infoOffre = group.offreDetails?.info_offre;
          const prixOffre = infoOffre?.prix_offre;
          const prixSpecialSoa = infoOffre?.prix_special_soa;
          const dureeOffre = infoOffre?.duree_offre;

          return (
            <Accordion
              key={group.offreId}
              defaultExpanded
              sx={{
                mb: 2,
                borderRadius: '16px !important',
                boxShadow: '0px 4px 12px rgba(0,0,0,0.03)',
                overflow: 'hidden',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#4318FF' }} />}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ width: '100%', pr: 2 }}
                >
                  <Avatar
                    sx={{ bgcolor: 'rgba(67, 24, 255, 0.1)', color: '#4318FF' }}
                  >
                    <EventNoteIcon />
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: '#2B3674' }}
                    >
                      {group.titreOffre}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#A3AED0' }}>
                      {[1, 2].includes(userRole) &&
                        `${group.intervenantDisplay} | `}
                      {prixOffre ? `Prix: ${prixOffre} €` : ''}
                      {prixSpecialSoa ? ` | Prix SOA: ${prixSpecialSoa} €` : ''}
                      {dureeOffre ? ` | Durée: ${dureeOffre} min` : ''}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${group.reservations.length} réservation(s)`}
                    sx={{ bgcolor: '#4318FF', color: '#fff', fontWeight: 600 }}
                  />
                </Stack>
              </AccordionSummary>

              <Divider />

              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table>
                    <TableHead sx={{ bgcolor: '#F4F7FE' }}>
                      <TableRow>
                        <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                          CLIENT / PATIENT
                        </TableCell>
                        <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                          DATE DE CRÉATION
                        </TableCell>
                        <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                          DÉBUT SÉANCE
                        </TableCell>
                        <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                          FIN SÉANCE
                        </TableCell>
                        <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                          STATUT
                        </TableCell>
                        <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                          COMPTE RENDU
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#A3AED0',
                            fontWeight: 700,
                            textAlign: 'center',
                          }}
                        >
                          ACTIONS
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.reservations.map((res) => {
                        const clientName = res.client
                          ? `${res.client.prenom_client || ''} ${res.client.nom_client || ''}`.trim()
                          : 'Client Inconnu';
                        const clientEmail = res.client?.email_client || '-';
                        const hasCompteRendu =
                          res.comptes_rendus && res.comptes_rendus.length > 0;

                        return (
                          <TableRow key={res.id_offre_reservation} hover>
                            <TableCell>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                              >
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: '#2B3674',
                                    fontSize: '12px',
                                  }}
                                >
                                  <PersonIcon fontSize="small" />
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, color: '#2B3674' }}
                                  >
                                    {clientName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: '#A3AED0' }}
                                  >
                                    {clientEmail}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>

                            <TableCell
                              sx={{ color: '#A3AED0', fontWeight: 500 }}
                            >
                              {res.created_at
                                ? new Date(res.created_at).toLocaleString(
                                    'fr-FR',
                                    { dateStyle: 'short', timeStyle: 'short' }
                                  )
                                : '-'}
                            </TableCell>

                            <TableCell
                              sx={{ color: '#2B3674', fontWeight: 500 }}
                            >
                              {new Date(
                                res.start_time_reservation
                              ).toLocaleString('fr-FR', {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              })}
                            </TableCell>

                            <TableCell
                              sx={{ color: '#2B3674', fontWeight: 500 }}
                            >
                              {new Date(
                                res.end_time_reservation
                              ).toLocaleString('fr-FR', {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              })}
                            </TableCell>

                            <TableCell>
                              {renderStatusChip(res.status_reservation)}
                            </TableCell>

                            <TableCell>
                              {hasCompteRendu ? (
                                <Chip
                                  icon={<DescriptionIcon />}
                                  label={`${res.comptes_rendus.length} Fiche(s)`}
                                  size="small"
                                  clickable
                                  onClick={() =>
                                    handleOpenCRModal(
                                      res.comptes_rendus[0],
                                      res
                                    )
                                  }
                                  sx={{
                                    fontWeight: 700,
                                    bgcolor: '#EBF3FE',
                                    color: '#4318FF',
                                    border: '1px solid #D1E2FF',
                                    '&:hover': { bgcolor: '#D0E2FF' },
                                  }}
                                />
                              ) : (
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#A3AED0' }}
                                >
                                  Aucun
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ textAlign: 'center' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handleOpenDetails(res)}
                                sx={{
                                  borderColor: '#4318FF',
                                  color: '#4318FF',
                                  borderRadius: '10px',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  '&:hover': {
                                    bgcolor: 'rgba(67, 24, 255, 0.05)',
                                    borderColor: '#4318FF',
                                  },
                                }}
                              >
                                Détails
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          );
        })
      )}

      {/* MODALE 1 : DÉTAILS AGRANDIE ET PROFESSIONNELLE MÉDICALE */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            p: 2.5,
            maxHeight: '90vh',
            bgcolor: '#FAFCFF',
          },
        }}
      >
        {selectedReservation &&
          (() => {
            const client = selectedReservation.client || {};
            const infoOffre = selectedReservation.offre?.info_offre || {};

            // Extraction robuste des aidants depuis patient_profile ou client
            const aidantsArray =
              client.patient_profile?.aidants ||
              client.aidants ||
              selectedReservation.aidants ||
              [];

            const hasAidants =
              Array.isArray(aidantsArray) && aidantsArray.length > 0;

            let joursOffArray = [];
            try {
              joursOffArray =
                typeof infoOffre.jours_off === 'string'
                  ? JSON.parse(infoOffre.jours_off)
                  : infoOffre.jours_off || [];
            } catch (e) {
              joursOffArray = [];
            }

            const labelSx = {
              color: '#718096',
              fontWeight: 700,
              fontSize: '0.75rem',
              mb: 0.5,
              letterSpacing: '0.5px',
            };

            return (
              <>
                {/* En-tête de la Modale */}
                <DialogTitle
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                    px: 1,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      sx={{
                        bgcolor: '#4318FF',
                        color: '#fff',
                        width: 48,
                        height: 48,
                        boxShadow: '0 4px 12px rgba(67, 24, 255, 0.3)',
                      }}
                    >
                      <HealthAndSafetyIcon fontSize="medium" />
                    </Avatar>
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 800, color: '#2B3674' }}
                        >
                          Dossier de Réservation #
                          {selectedReservation.id_offre_reservation}
                        </Typography>
                        {renderStatusChip(
                          selectedReservation.status_reservation
                        )}
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{ color: '#A3AED0', mt: 0.5 }}
                      >
                        Créée le{' '}
                        {selectedReservation.created_at
                          ? new Date(
                              selectedReservation.created_at
                            ).toLocaleString('fr-FR', {
                              dateStyle: 'full',
                              timeStyle: 'short',
                            })
                          : '-'}
                      </Typography>
                    </Box>
                  </Stack>
                  <IconButton
                    onClick={handleCloseModal}
                    sx={{
                      color: '#A3AED0',
                      bgcolor: '#F4F7FE',
                      '&:hover': { bgcolor: '#E2E8F0' },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>

                <Divider sx={{ my: 1.5 }} />

                <DialogContent sx={{ px: 1, py: 1 }}>
                  <Stack spacing={3}>
                    {/* Ligne 1 : Consultation & Patient & Aidants (conditionnel) */}
                    <Grid container spacing={2.5}>
                      {/* Carte 1 : Détails de la Séance */}
                      <Grid item xs={12} md={hasAidants ? 4 : 6}>
                        <Paper
                          sx={{
                            p: 2.5,
                            borderRadius: '18px',
                            bgcolor: '#ffffff',
                            border: '1px solid #E2E8F0',
                            height: '100%',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 800,
                              color: '#2B3674',
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <CalendarTodayIcon
                              fontSize="small"
                              sx={{ color: '#4318FF' }}
                            />
                            Détails de la Consultation
                          </Typography>
                          <Stack spacing={2}>
                            <Box>
                              <Typography variant="caption" sx={labelSx}>
                                SÉANCE DÉBUT
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: '#2B3674',
                                  fontSize: '0.95rem',
                                }}
                              >
                                {new Date(
                                  selectedReservation.start_time_reservation
                                ).toLocaleString('fr-FR', {
                                  dateStyle: 'medium',
                                  timeStyle: 'short',
                                })}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={labelSx}>
                                SÉANCE FIN
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: '#2B3674',
                                  fontSize: '0.95rem',
                                }}
                              >
                                {new Date(
                                  selectedReservation.end_time_reservation
                                ).toLocaleString('fr-FR', {
                                  dateStyle: 'medium',
                                  timeStyle: 'short',
                                })}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={labelSx}>
                                {getRoleLabel(
                                  selectedReservation.user
                                ).toUpperCase()}{' '}
                                PRATICIEN
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700, color: '#4318FF' }}
                              >
                                {selectedReservation.user
                                  ? `${selectedReservation.user.prenom_user || ''} ${selectedReservation.user.nom_user || ''}`.trim()
                                  : 'Non spécifié'}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>

                      {/* Carte 2 : Informations du Patient */}
                      <Grid item xs={12} md={hasAidants ? 4 : 6}>
                        <Paper
                          sx={{
                            p: 2.5,
                            borderRadius: '18px',
                            bgcolor: '#ffffff',
                            border: '1px solid #E2E8F0',
                            height: '100%',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 800,
                              color: '#2B3674',
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <PersonIcon
                              fontSize="small"
                              sx={{ color: '#05CD99' }}
                            />
                            Informations Patient
                          </Typography>
                          <Stack spacing={1.5}>
                            <Box>
                              <Typography variant="caption" sx={labelSx}>
                                PATIENT / CLIENT
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 800,
                                  color: '#1A202C',
                                  fontSize: '1.05rem',
                                }}
                              >
                                {`${client.prenom_client || ''} ${client.nom_client || ''}`.trim() ||
                                  'Client Inconnu'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={labelSx}>
                                CONTACT EMAIL
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={0.8}
                                alignItems="center"
                              >
                                <EmailIcon
                                  fontSize="small"
                                  sx={{ color: '#A3AED0', fontSize: 18 }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, color: '#2B3674' }}
                                >
                                  {client.email_client || 'Non renseigné'}
                                </Typography>
                              </Stack>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={labelSx}>
                                TÉLÉPHONE
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={0.8}
                                alignItems="center"
                              >
                                <PhoneIcon
                                  fontSize="small"
                                  sx={{ color: '#A3AED0', fontSize: 18 }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, color: '#2B3674' }}
                                >
                                  {client.telephone_client ||
                                    client.phone_client ||
                                    'Non renseigné'}
                                </Typography>
                              </Stack>
                            </Box>
                            {(client.addresse_client ||
                              client.adresse_client) && (
                              <Box>
                                <Typography variant="caption" sx={labelSx}>
                                  DOMICILE
                                </Typography>
                                <Stack
                                  direction="row"
                                  spacing={0.8}
                                  alignItems="center"
                                >
                                  <HomeIcon
                                    fontSize="small"
                                    sx={{ color: '#A3AED0', fontSize: 18 }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: '#2B3674' }}
                                  >
                                    {client.addresse_client ||
                                      client.adresse_client}
                                  </Typography>
                                </Stack>
                              </Box>
                            )}
                          </Stack>
                        </Paper>
                      </Grid>

                      {/* Carte 3 : Aidants & Proches (Affichée uniquement si présents) */}
                      {hasAidants && (
                        <Grid item xs={12} md={4}>
                          <Paper
                            sx={{
                              p: 2.5,
                              borderRadius: '18px',
                              bgcolor: '#F0F7FF',
                              border: '1px solid #BEE3F8',
                              height: '100%',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 800,
                                color: '#2B6CB0',
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <SupervisorAccountIcon
                                fontSize="small"
                                sx={{ color: '#3182CE' }}
                              />
                              Aidants & Proches
                            </Typography>

                            <Stack spacing={2}>
                              {aidantsArray.map((aidant, idx) => {
                                const isPrincipal = Boolean(
                                  aidant.pivot?.est_principal
                                );
                                const lien =
                                  aidant.pivot?.lien_patient_aidant || 'Proche';

                                return (
                                  <Box
                                    key={aidant.id_aidant || idx}
                                    sx={{
                                      p: 1.5,
                                      bgcolor: '#ffffff',
                                      borderRadius: '12px',
                                      border: '1px solid #E2E8F0',
                                    }}
                                  >
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      mb={1}
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          fontWeight: 800,
                                          color: '#2D3748',
                                        }}
                                      >
                                        {aidant.prenom_aidant}{' '}
                                        {aidant.nom_aidant}
                                      </Typography>
                                      <Stack direction="row" spacing={0.5}>
                                        <Chip
                                          label={lien}
                                          size="small"
                                          sx={{
                                            bgcolor: '#EBF8FF',
                                            color: '#2B6CB0',
                                            fontWeight: 700,
                                            height: 20,
                                            fontSize: '0.7rem',
                                          }}
                                        />
                                        {isPrincipal && (
                                          <Chip
                                            icon={
                                              <StarIcon
                                                sx={{
                                                  fontSize: '12px !important',
                                                  color: '#D69E2E !important',
                                                }}
                                              />
                                            }
                                            label="Principal"
                                            size="small"
                                            sx={{
                                              bgcolor: '#FEFCBF',
                                              color: '#744210',
                                              fontWeight: 800,
                                              height: 20,
                                              fontSize: '0.7rem',
                                            }}
                                          />
                                        )}
                                      </Stack>
                                    </Stack>

                                    <Stack spacing={0.5}>
                                      {aidant.telephone_aidant && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: '#4A5568',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                          }}
                                        >
                                          <PhoneIcon
                                            sx={{
                                              fontSize: 14,
                                              color: '#A0AEC0',
                                            }}
                                          />{' '}
                                          {aidant.telephone_aidant}
                                        </Typography>
                                      )}
                                      {aidant.email_aidant && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: '#4A5568',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                          }}
                                        >
                                          <EmailIcon
                                            sx={{
                                              fontSize: 14,
                                              color: '#A0AEC0',
                                            }}
                                          />{' '}
                                          {aidant.email_aidant}
                                        </Typography>
                                      )}
                                      {aidant.profession_aidant && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: '#718096',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                          }}
                                        >
                                          <WorkIcon
                                            sx={{
                                              fontSize: 14,
                                              color: '#A0AEC0',
                                            }}
                                          />{' '}
                                          Profession: {aidant.profession_aidant}
                                        </Typography>
                                      )}
                                      {aidant.adresse_aidant && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: '#718096',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                          }}
                                        >
                                          <HomeIcon
                                            sx={{
                                              fontSize: 14,
                                              color: '#A0AEC0',
                                            }}
                                          />{' '}
                                          {aidant.adresse_aidant}
                                          {aidant.ville_aidant
                                            ? `, ${aidant.ville_aidant}`
                                            : ''}
                                        </Typography>
                                      )}
                                    </Stack>
                                  </Box>
                                );
                              })}
                            </Stack>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>

                    {/* Ligne 2 : Fiche complète de la Prestation Medicale */}
                    <Paper
                      sx={{
                        p: 3,
                        bgcolor: '#ffffff',
                        borderRadius: '18px',
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 800,
                          mb: 3,
                          color: '#2B3674',
                          borderLeft: '4px solid #4318FF',
                          pl: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <LocalOfferIcon sx={{ color: '#4318FF' }} />
                        Spécifications de la Prestation / Soin
                      </Typography>

                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" sx={labelSx}>
                            TITRE DE LA PRESTATION
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: '1.2rem',
                              color: '#1A202C',
                            }}
                          >
                            {infoOffre.titre_offre || 'N/A'}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" sx={labelSx}>
                            MODE DE CONSULTATION / FORMAT
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            mt={0.5}
                          >
                            <Chip
                              icon={<VideocamIcon fontSize="small" />}
                              label={
                                infoOffre.type_offre
                                  ? infoOffre.type_offre.replace('_', ' ')
                                  : 'N/A'
                              }
                              size="small"
                              sx={{
                                bgcolor: '#EDF2F7',
                                fontWeight: 700,
                                textTransform: 'capitalize',
                                px: 1,
                              }}
                            />
                            {infoOffre.format_offre && (
                              <Chip
                                label={`Format : ${infoOffre.format_offre}`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: '#CBD5E0',
                                  fontWeight: 700,
                                  px: 1,
                                }}
                              />
                            )}
                          </Stack>
                        </Grid>

                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" sx={labelSx}>
                            HONORAIRES STANDARD
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 900,
                              fontSize: '1.3rem',
                              color: '#38A169',
                            }}
                          >
                            {infoOffre.prix_offre
                              ? `${infoOffre.prix_offre} €`
                              : 'N/A'}
                          </Typography>
                        </Grid>

                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" sx={labelSx}>
                            TARIF SPÉCIAL S.O.A
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 900,
                              fontSize: '1.3rem',
                              color: '#4318FF',
                            }}
                          >
                            {infoOffre.prix_special_soa
                              ? `${infoOffre.prix_special_soa} €`
                              : 'N/A'}
                          </Typography>
                        </Grid>

                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" sx={labelSx}>
                            DURÉE DE CONSULTATION
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <AccessTimeIcon
                              fontSize="small"
                              sx={{ color: '#4A5568' }}
                            />
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: '1.1rem',
                                color: '#2D3748',
                              }}
                            >
                              {infoOffre.duree_offre
                                ? `${infoOffre.duree_offre} min`
                                : 'N/A'}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" sx={labelSx}>
                            TEMPS DE PAUSE
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <ScheduleIcon
                              fontSize="small"
                              sx={{ color: '#4A5568' }}
                            />
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: '1.1rem',
                                color: '#2D3748',
                              }}
                            >
                              {infoOffre.duree_pause
                                ? `${infoOffre.duree_pause} min`
                                : 'N/A'}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid item xs={12}>
                          <Divider sx={{ my: 0.5 }} />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" sx={labelSx}>
                            PLAGES HORAIRES
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.95rem',
                              color: '#4A5568',
                              mt: 0.5,
                            }}
                          >
                            • Matin :{' '}
                            <strong>
                              {infoOffre.plage_horaire_matin_debut?.slice(
                                0,
                                5
                              ) || 'N/A'}{' '}
                              -{' '}
                              {infoOffre.plage_horaire_matin_fin?.slice(0, 5) ||
                                'N/A'}
                            </strong>
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.95rem',
                              color: '#4A5568',
                            }}
                          >
                            • Après-midi :{' '}
                            <strong>
                              {infoOffre.plage_horaire_aprem_debut?.slice(
                                0,
                                5
                              ) || 'N/A'}{' '}
                              -{' '}
                              {infoOffre.plage_horaire_aprem_fin?.slice(0, 5) ||
                                'N/A'}
                            </strong>
                          </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" sx={labelSx}>
                            JOURS DE NON-DISPONIBILITÉ (JOURS OFF)
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            flexWrap="wrap"
                            useFlexGap
                            mt={0.8}
                          >
                            {joursOffArray.length > 0 ? (
                              joursOffArray.map((jour, index) => (
                                <Chip
                                  key={index}
                                  label={jour}
                                  size="small"
                                  icon={<EventBusyIcon fontSize="small" />}
                                  sx={{
                                    bgcolor: '#FFF5F5',
                                    color: '#C53030',
                                    fontWeight: 700,
                                    textTransform: 'capitalize',
                                  }}
                                />
                              ))
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Aucun jour indisponible
                              </Typography>
                            )}
                          </Stack>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="caption" sx={labelSx}>
                            LIEU / ADRESSE DE LA CONSULTATION
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            mt={0.5}
                          >
                            <LocationOnIcon
                              fontSize="small"
                              sx={{ color: '#EE5D50' }}
                            />
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: '1rem',
                                color: '#2D3748',
                              }}
                            >
                              {infoOffre.location_type === 'locaux_soa'
                                ? 'Locaux S.O.A'
                                : infoOffre.address_ws || 'Lieu non spécifié'}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="caption" sx={labelSx}>
                            DESCRIPTION ET PROTOCOLE DE LA PRESTATION
                          </Typography>
                          <Paper
                            sx={{
                              mt: 1,
                              p: 2,
                              bgcolor: '#F8FAFC',
                              borderRadius: '12px',
                              border: '1px solid #E2E8F0',
                              boxShadow: 'none',
                            }}
                          >
                            <Typography
                              sx={{
                                color: '#4A5568',
                                fontSize: '0.95rem',
                                lineHeight: 1.6,
                                whiteSpace: 'pre-line',
                              }}
                            >
                              {infoOffre.description_offre ||
                                'Aucune description fournie pour cette offre.'}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Stack>
                </DialogContent>

                <DialogActions
                  sx={{ px: 2, pt: 2, pb: 1, justifyContent: 'flex-end' }}
                >
                  <Button
                    onClick={handleCloseModal}
                    variant="contained"
                    sx={{
                      bgcolor: '#2B3674',
                      borderRadius: '12px',
                      px: 4,
                      fontWeight: 700,
                      textTransform: 'none',
                      '&:hover': { bgcolor: '#1B254B' },
                    }}
                  >
                    Fermer
                  </Button>
                </DialogActions>
              </>
            );
          })()}
      </Dialog>

      {/* MODALE 2 : COMPTE RENDU */}
      <Dialog
        open={openCRModal}
        onClose={handleCloseCRModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '24px', p: 2 } }}
      >
        {selectedCR && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 1,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar
                  sx={{ bgcolor: 'rgba(67, 24, 255, 0.1)', color: '#4318FF' }}
                >
                  <DescriptionIcon />
                </Avatar>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: '#2B3674' }}
                  >
                    Détails du Compte Rendu -
                    {selectedCR.numero_seance || 1}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#A3AED0' }}>
                    Créé le{' '}
                    {selectedCR.created_at
                      ? new Date(selectedCR.created_at).toLocaleString('fr-FR')
                      : '-'}
                  </Typography>
                </Box>
              </Stack>
              <IconButton
                onClick={handleCloseCRModal}
                sx={{ color: '#A3AED0' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <Divider sx={{ my: 1 }} />

            <DialogContent sx={{ mt: 1 }}>
              <Box id="printable-compte-rendu">
                <div
                  className="header"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '2px solid #4318FF',
                    paddingBottom: '10px',
                    marginBottom: '20px',
                  }}
                >
                  <div>
                    <div
                      className="title"
                      style={{
                        fontSize: '20px',
                        fontWeight: '800',
                        color: '#4318FF',
                      }}
                    >
                      PLATEFORME S.O.A
                    </div>
                    <div
                      className="subtitle"
                      style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#2B3674',
                      }}
                    >
                      Compte Rendu & Suivi
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#718096',
                        marginTop: '4px',
                      }}
                    >
                      Prestation :{' '}
                      <strong>
                        {selectedCR.titre_soin ||
                          crReservationParent?.offre?.info_offre?.titre_offre ||
                          'N/A'}
                      </strong>
                    </div>
                  </div>
                  <div>
                    <span
                      className="badge"
                      style={{
                        background: '#E6F9F5',
                        color: '#05CD99',
                        padding: '6px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        border: '1px solid #B2F1E4',
                      }}
                    >
                      VALIDÉ & SIGNÉ
                    </span>
                  </div>
                </div>

                <Paper
                  className="card"
                  sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: '16px',
                    bgcolor: '#F4F7FE',
                    boxShadow: 'none',
                  }}
                >
                  <Grid container spacing={2} className="grid">
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: '#2B3674' }}>
                        <strong>Séance :</strong>
                        {selectedCR.numero_seance || 1}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: '#2B3674' }}>
                        <strong>
                          {getRoleLabel(crReservationParent?.user)} :
                        </strong>{' '}
                        {crReservationParent?.user
                          ? `${crReservationParent.user.prenom_user || ''} ${crReservationParent.user.nom_user || ''}`.trim()
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: '#2B3674' }}>
                        <strong>Date de consultation :</strong>{' '}
                        {crReservationParent?.start_time_reservation
                          ? new Date(
                              crReservationParent.start_time_reservation
                            ).toLocaleString('fr-FR')
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: '#2B3674' }}>
                        <strong>Date de rédaction :</strong>{' '}
                        {selectedCR.created_at
                          ? new Date(selectedCR.created_at).toLocaleString(
                              'fr-FR'
                            )
                          : 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                <Paper
                  className="section sec-1"
                  sx={{
                    p: 2.5,
                    mb: 2,
                    borderRadius: '12px',
                    bgcolor: '#F8FAFC',
                    borderLeft: '4px solid #0284C7',
                    boxShadow: 'none',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    className="sec-title"
                    sx={{
                      fontWeight: 700,
                      color: '#0284C7',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <PsychologyIcon fontSize="small" /> Pratique & Approche
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#334155', whiteSpace: 'pre-line' }}
                  >
                    {selectedCR.pratique_approche ||
                      selectedCR.pratique ||
                      'Aucune donnée saisie.'}
                  </Typography>
                </Paper>

                <Paper
                  className="section sec-2"
                  sx={{
                    p: 2.5,
                    mb: 2,
                    borderRadius: '12px',
                    bgcolor: '#F0F9FF',
                    borderLeft: '4px solid #0EA5E9',
                    boxShadow: 'none',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    className="sec-title"
                    sx={{
                      fontWeight: 700,
                      color: '#0EA5E9',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <FaceIcon fontSize="small" /> Perception & État du Patient
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#334155', whiteSpace: 'pre-line' }}
                  >
                    {selectedCR.perception_patient ||
                      selectedCR.perception ||
                      'Aucune donnée saisie.'}
                  </Typography>
                </Paper>

                <Paper
                  className="section sec-3"
                  sx={{
                    p: 2.5,
                    mb: 2,
                    borderRadius: '12px',
                    bgcolor: '#F0FDF4',
                    borderLeft: '4px solid #10B981',
                    boxShadow: 'none',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    className="sec-title"
                    sx={{
                      fontWeight: 700,
                      color: '#10B981',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <MedicalServicesIcon fontSize="small" /> Résumé & Actes
                    Réalisés
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#334155', whiteSpace: 'pre-line' }}
                  >
                    {selectedCR.resume_seance ||
                      selectedCR.resume ||
                      'Aucune donnée saisie.'}
                  </Typography>
                </Paper>

                <Paper
                  className="section sec-4"
                  sx={{
                    p: 2.5,
                    borderRadius: '12px',
                    bgcolor: '#EEF2FF',
                    borderLeft: '4px solid #6366F1',
                    boxShadow: 'none',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    className="sec-title"
                    sx={{
                      fontWeight: 700,
                      color: '#6366F1',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <CalendarTodayIcon fontSize="small" /> Plan de Soins & Suite
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#334155', whiteSpace: 'pre-line' }}
                  >
                    {selectedCR.prevision_suite ||
                      selectedCR.plan ||
                      'Aucune donnée saisie.'}
                  </Typography>
                </Paper>
              </Box>
            </DialogContent>

            <DialogActions
              sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}
            >
              <Button
                onClick={handleCloseCRModal}
                sx={{ color: '#A3AED0', fontWeight: 600 }}
              >
                Fermer
              </Button>
              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                onClick={handlePrintPDF}
                sx={{
                  bgcolor: '#4318FF',
                  borderRadius: '12px',
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#3311CC' },
                }}
              >
                Télécharger en PDF / Imprimer
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default HistoriqueReservations;
